import { useMemo } from "react";
import trpc from "../utils/trpc.js";
import { buildItemLink } from "../utils/canvas.js";
import useCurrentModuleItemId from "./use-current-module-item-id.js";
import useCompletedByItemId from "./use-completed-by-item-id.js";

export function useLearningItem() {
  const currentModuleItemId = useCurrentModuleItemId();
  const { completedByItemId } = useCompletedByItemId();

  const { data: modules } = trpc.course.getModules.useQuery(undefined, {
    staleTime: Infinity,
    enabled: Boolean(currentModuleItemId),
  });

  const { currentModuleItemTitle, isCurrentOptional } = useMemo(() => {
    if (!modules || !currentModuleItemId) return { currentModuleItemTitle: null, isCurrentOptional: false };
    for (const mod of modules) {
      for (const sub of mod.subModules) {
        const found = sub.items.find(it => String(it.id) === currentModuleItemId);
        if (found) return { currentModuleItemTitle: found.title ?? null, isCurrentOptional: sub.required === false };
      }
    }
    return { currentModuleItemTitle: null, isCurrentOptional: false };
  }, [modules, currentModuleItemId]);

  const { previousItemLink, nextItemLink, nextItem } = useMemo(() => {
    if (!modules || !currentModuleItemId) {
      return { previousItemLink: null, nextItemLink: null, nextItem: null };
    }
    const flatItems = modules.flatMap(mod => mod.subModules.flatMap(sub => sub.items));
    const currentIndex = flatItems.findIndex(it => String(it.id) === currentModuleItemId);
    if (currentIndex === -1) return { previousItemLink: null, nextItemLink: null, nextItem: null };
    const previousItem = flatItems[currentIndex - 1] ?? null;
    const nextItem = flatItems[currentIndex + 1] ?? null;
    return {
      previousItemLink: previousItem ? buildItemLink(previousItem) : null,
      nextItemLink: nextItem ? buildItemLink(nextItem) : null,
      nextItem,
    };
  }, [modules, currentModuleItemId]);

  const isCurrentCompleted = Boolean(currentModuleItemId) && completedByItemId.has(String(currentModuleItemId));

  return {
    currentModuleItemId,
    currentModuleItemTitle,
    isCurrentOptional,
    previousItemLink,
    nextItemLink,
    nextItem,
    isCurrentCompleted,
  };
}
